import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuth } from '@/hooks/use-auth'
import { formatAddressDisplay } from '@/lib/address'
import { openBiddleDetail } from '@/lib/biddle-navigation'
import { db } from '@/lib/firebase'
import { formatAud, relativeTime } from '@/lib/utils'
import {
  biddleStatus,
  isBiddleActive,
  parseBiddle,
  parseProperty,
  type Biddle,
  type Property,
} from '@/types/models'

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

export function MyPropertyBiddlesPage() {
  return (
    <RequireAuth>
      <PropertyBiddles />
    </RequireAuth>
  )
}

function PropertyBiddles() {
  const { propertyId } = useParams<{ propertyId: string }>()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [property, setProperty] = useState<Property | null>(null)
  const [biddles, setBiddles] = useState<Biddle[]>([])
  const [allowed, setAllowed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const tagsKey = (profile?.receivedBiddles ?? []).join('|')

  useEffect(() => {
    if (!propertyId || !user) {
      setLoading(false)
      return
    }
    const tags = tagsKey ? tagsKey.split('|') : []
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const ownedSnap = await getDocs(
          query(
            collection(db, 'users', user.uid, 'propertiesOwned'),
            where('propertyID', '==', propertyId),
          ),
        )
        if (ownedSnap.empty) {
          if (!cancelled) {
            setAllowed(false)
            setError('You do not own this property.')
          }
          return
        }
        setAllowed(true)

        const propertySnap = await getDoc(doc(db, 'properties', propertyId))
        if (cancelled) return
        setProperty(
          propertySnap.exists()
            ? parseProperty(propertySnap.id, propertySnap.data() as Record<string, unknown>)
            : null,
        )

        // Same query shape as iOS PropertyOwnerDataManager.fetchAllBiddles —
        // rules require ownerBiddleTag ∈ receivedBiddles (not propertyID alone).
        const fetched: Biddle[] = []
        for (const part of chunk(tags, 30)) {
          if (part.length === 0) continue
          const q = query(
            collection(db, 'biddles'),
            where('ownerBiddleTag', 'in', part),
            where('isDeleted', '==', false),
          )
          const snap = await getDocs(q)
          snap.docs.forEach((d: QueryDocumentSnapshot) => {
            const biddle = parseBiddle(d.id, d.data() as Record<string, unknown>)
            if (biddle.propertyID === propertyId) fetched.push(biddle)
          })
        }

        if (cancelled) return
        fetched.sort((a, b) => b.timeCreated.getTime() - a.timeCreated.getTime())
        setBiddles(fetched)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load Biddles')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [propertyId, user, tagsKey])

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!allowed || error) {
    return (
      <EmptyState
        title="Unable to load Biddles"
        description={error ?? 'This property isn’t linked to your account.'}
        actionLabel="Back to properties"
        onAction={() => {
          window.location.href = '/properties'
        }}
      />
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <Link to="/properties" className="text-sm font-medium text-biddle-blue">
          ← Properties
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-navy">Incoming Biddles</h1>
        <p className="mt-1 text-sm text-muted">
          {property ? formatAddressDisplay(property.fullAddress) : propertyId}
        </p>
      </div>

      {biddles.length === 0 ? (
        <EmptyState
          title="No Biddles yet"
          description="When a buyer submits interest on this property, it will appear here."
        />
      ) : (
        <ul className="space-y-3">
          {biddles.map((biddle) => {
            const status = biddleStatus(biddle)
            return (
              <li key={biddle.id}>
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => {
                    if (propertyId) openBiddleDetail(navigate, propertyId, biddle.id)
                  }}
                >
                  <Card className="transition hover:border-biddle-blue">
                    <CardContent className="flex items-start justify-between gap-3 pt-5">
                      <div>
                        <p className="font-semibold text-navy">{formatAud(biddle.price)}</p>
                        <p className="mt-1 text-sm text-muted">
                          {relativeTime(biddle.timeCreated)}
                          {!isBiddleActive(biddle) ? ' · Inactive' : ''}
                        </p>
                      </div>
                      <Badge tone={status}>{status}</Badge>
                    </CardContent>
                  </Card>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
